/* eslint-disable no-param-reassign */
/* eslint-disable no-console */
import * as Sentry from '@sentry/react';
import { AxiosError } from 'axios';

function sanitizeRequestUrl(requestUrl: string): { sanitizedUrl: string; requestPath: string } {
  try {
    const urlObject = new URL(requestUrl);
    const requestPath = urlObject.pathname || requestUrl;
    const sanitizedUrl = urlObject.origin ? `${urlObject.origin}${urlObject.pathname}` : requestPath;

    return { sanitizedUrl, requestPath };
  } catch {
    try {
      const urlObject = new URL(requestUrl, 'http://localhost');
      const requestPath = urlObject.pathname || requestUrl.split('?')[0];

      return { sanitizedUrl: requestPath, requestPath };
    } catch {
      const sanitizedUrl = requestUrl.split('?')[0];

      return { sanitizedUrl, requestPath: sanitizedUrl };
    }
  }
}

export default function sentryBeforeSendFilter(event: Sentry.ErrorEvent, hint: Sentry.EventHint) {
  const exception = event.exception?.values?.[0];
  const originalException = hint.originalException as Error;

  const isAxiosError =
    originalException && originalException instanceof Error && (originalException as AxiosError).isAxiosError;
  const isRequestError =
    (exception && exception.type === 'Error' && /HTTP Client Error/gm.test(exception.value) && event.request) ||
    isAxiosError;
  let requestUrl;

  if (isRequestError) {
    if (isAxiosError) {
      requestUrl = (originalException as AxiosError)?.config?.url;
    } else {
      requestUrl = event.request?.url;
    }
  }

  // Ignore 401 errors
  if (isAxiosError && (originalException as AxiosError)?.response?.status === 401) {
    console.log('Sentry ignore error because its a 401 request error', event, hint);
    return null;
  }

  // Ignore errors that are warnings
  if (event.message && /^warning/gim.test(event.message)) {
    console.log('Sentry ignore error because its a warning', event, hint);
    return null;
  }

  // For request error if possible we rewrite the error message
  if (isRequestError && requestUrl) {
    try {
      const { sanitizedUrl, requestPath } = sanitizeRequestUrl(requestUrl);
      const requestMethod = event.request.method || '?';
      const requestStatus = event.message.substring(event.message.length - 3) || '?';
      const newMessage = `API Error (${requestStatus}): ${requestMethod} ${requestPath}`;

      if (!event.extra) {
        event.extra = {};
      }

      // Strip the query string everywhere it is stored: query params can carry
      // tokens or other PII (the raw searchParams were previously leaked verbatim).
      event.extra.requestUrl = sanitizedUrl;
      event.extra.originalMessage = event.message;
      event.message = newMessage;

      // event.request.url is populated with the full URL (incl. query) by the
      // http client integration; overwrite it with the sanitized value.
      if (event.request?.url) {
        event.request.url = sanitizedUrl;
      }
    } catch (e) {
      // ignore
    }
  }

  // Privacy backstop: email is no longer set on the Sentry user (see
  // use-sentry-user-analytics.hook), but strip it here anyway in case an
  // integration or future code path attaches it.
  if (event.user) {
    // eslint-disable-next-line no-param-reassign
    delete event.user.email;
  }

  // On development environment we log the error to the console for easier debugging
  if (process.env.NODE_ENV === 'development') {
    console.info('Sending Error to Sentry', event, hint);
  }

  return event;
}
